import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Logo from '../../components/Logo';
import SEO from '../../components/SEO';
import { Target, BarChart3, Users, Calendar, TrendingUp, Settings, ChevronRight, Check, X } from 'lucide-react';

// Hook pour animations au scroll
const useInView = () => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1 });

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return [ref, isInView];
};

// Composant pour sections animées
const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className={`transform transition-all duration-700 ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)'
      }}
    >
      {children}
    </div>
  );
};

const Pilote = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // SEO Configuration
  const seoConfig = {
    title: 'Direction Commerciale Externalisée - FOMOW | Pilotage Opérationnel',
    description: 'Direction commerciale à temps partagé pour startups et PME. Pilotage KPIs, management équipes, déploiement terrain. Transformez votre stratégie en CA.',
    path: '/services/pilote',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Direction Commerciale Externalisée",
      "description": "Pilotage opérationnel et management commercial externalisé",
      "provider": { "@type": "Organization", "name": "FOMOW" },
      "serviceType": "Business Consulting"
    }
  };

  // Domaines d'intervention
  const expertises = [
    { 
      icon: Target, 
      title: 'Définition des objectifs', 
      desc: 'KPIs, quotas, jalons de progression',
      details: ['Objectifs SMART par équipe', 'Cascade des objectifs', 'Indicateurs de performance']
    },
    { 
      icon: Calendar, 
      title: 'Planification opérationnelle', 
      desc: 'Actions, deadlines, responsabilités',
      details: ['Planning hebdomadaire', 'Répartition des tâches', 'Coordination inter-équipes']
    },
    { 
      icon: BarChart3, 
      title: 'Pilotage des KPIs', 
      desc: 'Suivi, analyse, ajustements',
      details: ['Dashboards temps réel', 'Alertes automatisées', 'Analyse des écarts']
    },
    { 
      icon: Users, 
      title: 'Management opérationnel', 
      desc: 'Animation, coaching, motivation',
      details: ['Réunions d\'équipe', 'One-to-one réguliers', 'Gestion de la performance']
    },
    { 
      icon: TrendingUp, 
      title: 'Exécution commerciale', 
      desc: 'Prospection, closing, account management',
      details: ['Suivi du pipeline', 'Revue des deals', 'Forecast précis']
    },
    { 
      icon: Settings, 
      title: 'Optimisation continue', 
      desc: 'Process, outils, méthodes',
      details: ['Identification des blocages', 'Quick wins', 'Amélioration itérative']
    },
  ];

  // Phases de travail
  const phases = [
    { 
      num: '01', 
      title: 'Intégration', 
      desc: 'On s\'immerge dans votre réalité.',
      items: [
        'Compréhension de votre business et vos enjeux',
        'Rencontre avec les équipes et parties prenantes',
        'Audit rapide de l\'existant (process, outils, KPIs)'
      ]
    },
    { 
      num: '02', 
      title: 'Cadrage', 
      desc: 'On définit le cap ensemble.',
      items: [
        'Définition des objectifs prioritaires',
        'Construction du plan d\'action',
        'Mise en place des rituels de pilotage'
      ]
    },
    { 
      num: '03', 
      title: 'Exécution', 
      desc: 'On délivre à vos côtés.',
      items: [
        'Pilotage quotidien/hebdomadaire des actions',
        'Animation des équipes et suivi des résultats',
        'Ajustements en temps réel'
      ]
    },
    { 
      num: '04', 
      title: 'Transmission', 
      desc: 'On autonomise vos équipes.',
      items: [
        'Documentation des process et bonnes pratiques',
        'Montée en compétence des managers internes',
        'Passage de relais progressif'
      ]
    },
  ];

  // Comparaison
  const comparisons = [
    { bad: 'Des consultants qui observent et recommandent', good: 'Des opérationnels qui exécutent avec vous' },
    { bad: 'Des rapports livrés puis oubliés', good: 'Un pilotage quotidien orienté résultats' },
    { bad: 'Une position au-dessus ou en dehors', good: 'Une intégration au sein de votre équipe' },
    { bad: 'Des théories génériques', good: 'Une approche pragmatique adaptée à votre contexte' },
  ];

  // Exemples de missions
  const missions = [
    { 
      type: 'Scale-up en hypercroissance', 
      size: '80 personnes',
      challenge: 'Structurer et piloter une équipe sales passée de 5 à 20 en 6 mois',
      solution: 'Direction commerciale à temps partagé, rituels, dashboards, coaching managers',
      result: 'Objectifs atteints à 115%, turnover maîtrisé'
    },
    { 
      type: 'PME industrielle', 
      size: '150 personnes',
      challenge: 'Lancer une équipe de prospection outbound from scratch',
      solution: 'Recrutement, formation, process, pilotage opérationnel pendant 6 mois',
      result: '+2M€ de pipeline généré en 6 mois'
    },
    { 
      type: 'Startup B2B SaaS', 
      size: '25 personnes',
      challenge: 'Remplacer temporairement le Head of Sales parti',
      solution: 'Management intérimaire, continuité opérationnelle, préparation du recrutement',
      result: 'Transition sans rupture, nouveau Head of Sales recruté et onboardé'
    },
  ];

  // Formats d'engagement
  const formats = [
    {
      title: 'Direction à temps partagé',
      desc: 'Un directeur commercial expérimenté intégré à votre équipe 2-3 jours par semaine.',
      duration: 'Engagement minimum 3 mois',
      ideal: 'Scale-ups sans Head of Sales ou en transition'
    },
    {
      title: 'Management intérimaire',
      desc: 'Remplacement temporaire d\'un poste clé (Head of Sales, VP Sales, RevOps).',
      duration: 'Mission de 3 à 12 mois',
      ideal: 'Transition, recherche de profil, congé maternité/paternité'
    },
    {
      title: 'Pilotage de projet',
      desc: 'Exécution d\'un chantier spécifique : lancement, restructuration, nouveau marché.',
      duration: 'Durée variable selon le projet',
      ideal: 'Projets de transformation commerciale'
    },
  ];

  // FAQ
  const faqs = [
    { 
      q: 'Quelle est la différence avec le Sparring Partner ?', 
      a: 'Le Sparring Partner intervient au niveau stratégique : réflexion, prise de recul, décisions clés. La Direction Commerciale Externalisée est opérationnelle : on exécute, on pilote, on manage au quotidien.' 
    },
    { 
      q: 'Combien de temps par semaine êtes-vous présent ?', 
      a: 'Cela dépend du format choisi. En direction à temps partagé, généralement 2-3 jours par semaine. En management intérimaire, jusqu\'à temps plein selon les besoins.' 
    },
    { 
      q: 'Comment vous intégrez-vous à nos équipes ?', 
      a: 'On s\'intègre pleinement : accès aux outils, participation aux rituels, présence sur site ou en remote. On fait partie de l\'équipe, pas des consultants externes.' 
    },
    { 
      q: 'Que se passe-t-il à la fin de la mission ?', 
      a: 'On travaille dès le départ à rendre l\'organisation autonome. La transmission est progressive : documentation, formation des managers, passage de relais structuré.' 
    },
    { 
      q: 'Pouvez-vous intervenir sur des sujets hors ventes ?', 
      a: 'Notre focus est le revenue : ventes, RevOps, customer success. Pour d\'autres sujets, on vous oriente vers les bons partenaires.' 
    },
  ];

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      <SEO 
        title={seoConfig.title}
        description={seoConfig.description}
        path={seoConfig.path}
        structuredData={seoConfig.structuredData}
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-b from-[#041C2C] to-[#0a2a3d] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[#2A41E5]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#2A41E5]/5 rounded-full blur-2xl" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2A41E5]/20 border border-[#2A41E5]/30 mb-6">
              <span className="text-sm text-[#2A41E5] font-medium">Direction Commerciale</span>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={100}>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Direction Commerciale Externalisée
            </h1>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl leading-relaxed mb-4">
              Des leaders commerciaux expérimentés <span className="text-[#2A41E5] font-semibold">intégrés à votre équipe</span> pour exécuter, piloter et transformer.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={300}>
            <p className="text-lg text-white/60 max-w-2xl mb-10">
              Pas des consultants qui observent. Des opérationnels qui font. À vos côtés, engagés sur vos objectifs de revenus.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={400}>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              Contactez-nous
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Positionnement */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-4 block">Notre positionnement</span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Votre partenaire, pas votre prestataire
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Chaque membre de notre équipe est un cadre expérimenté dans les ventes ou les opérations revenue, avec <strong>10 à 20 ans d'expérience</strong> dont plusieurs années en direction commerciale dans des entreprises en forte croissance.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Nous avons dirigé des équipes, atteint des objectifs (et parfois les avons manqués), fait évoluer des organisations sous pression. <strong>Nous connaissons le métier de l'intérieur.</strong>
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={200}>
              <div className="bg-[#041C2C] rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-6">Ce que ça change</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-white/80">On ne se positionne ni au-dessus, ni en dehors. On travaille <strong className="text-white">avec</strong> vous.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-white/80">On s'engage sur votre feuille de route et vos objectifs de revenus.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-white/80">On délivre un impact mesurable, pas des slides de recommandations.</span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Domaines d'intervention */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Nos interventions</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-4 text-center">
              Ce sur quoi nous intervenons
            </h2>
            <p className="text-lg text-white/60 text-center max-w-3xl mx-auto mb-16">
              Du stratégique à l'opérationnel, nous couvrons l'ensemble du spectre de la direction commerciale.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertises.map((exp, idx) => {
              const IconComponent = exp.icon;
              return (
                <AnimatedSection key={idx} delay={idx * 100}>
                  <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-[#2A41E5]/50 transition-all duration-300 group h-full">
                    <div className="flex items-start gap-4 mb-4">
                      <IconComponent className="w-6 h-6 text-[#2A41E5] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-1">{exp.title}</h3>
                        <p className="text-white/60 text-sm">{exp.desc}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 mt-4 pt-4 border-t border-white/10">
                      {exp.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#2A41E5]" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comment on travaille */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Notre approche</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Une intégration progressive, un impact durable
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Notre objectif : vous rendre autonomes. Pas créer de dépendance.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 gap-8">
            {phases.map((phase, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="bg-[#F5F5F5] rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300 group border border-gray-100 hover:border-[#2A41E5]/30">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-[#2A41E5] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <span className="text-white font-bold text-lg">{phase.num}</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">{phase.title}</h3>
                      <p className="text-[#2A41E5] font-medium">{phase.desc}</p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {phase.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-600">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Formats d'engagement */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Formats</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Un modèle flexible, adapté à vos besoins
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Nos engagements vont de 3 à 12 mois, selon la taille, la maturité et la complexité de votre organisation.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {formats.map((format, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="bg-white rounded-2xl p-8 h-full border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-lg transition-all duration-300">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{format.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">{format.desc}</p>
                  <div className="space-y-3 pt-4 border-t border-gray-100">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Durée</p>
                      <p className="text-sm text-gray-700 font-medium">{format.duration}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Idéal pour</p>
                      <p className="text-sm text-[#2A41E5]">{format.ideal}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Ce qu'on fait / ne fait pas */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Des opérationnels, pas des consultants
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              La différence fondamentale : on ne vous dit pas quoi faire, on le fait avec vous.
            </p>
          </AnimatedSection>
          
          <div className="bg-[#F5F5F5] rounded-2xl overflow-hidden shadow-lg">
            <div className="grid md:grid-cols-2">
              {/* Ce qu'on NE fait PAS */}
              <div className="p-8 md:p-10 bg-gray-50">
                <h3 className="text-xl font-bold text-gray-400 mb-6 flex items-center gap-2">
                  <X className="w-6 h-6 text-red-400" />
                  Ce qu'on NE fait PAS
                </h3>
                <ul className="space-y-4">
                  {comparisons.map((comp, idx) => (
                    <AnimatedSection key={idx} delay={idx * 100}>
                      <li className="flex items-start gap-3 text-gray-500">
                        <span className="text-red-400 mt-1">✗</span>
                        {comp.bad}
                      </li>
                    </AnimatedSection>
                  ))}
                </ul>
              </div>
              
              {/* Ce qu'on FAIT */}
              <div className="p-8 md:p-10 bg-[#041C2C]">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Check className="w-6 h-6 text-green-400" />
                  Ce qu'on FAIT
                </h3>
                <ul className="space-y-4">
                  {comparisons.map((comp, idx) => (
                    <AnimatedSection key={idx} delay={idx * 100}>
                      <li className="flex items-start gap-3 text-white/80">
                        <span className="text-green-400 mt-1">✓</span>
                        {comp.good}
                      </li>
                    </AnimatedSection>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exemples de missions */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">Cas concrets</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              Quelques exemples de missions
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-16">
              Chaque mission est différente. Voici ce que nous avons réalisé pour d'autres.
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8">
            {missions.map((mission, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="bg-white rounded-2xl p-8 h-full border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-lg transition-all duration-300">
                  <div className="mb-6">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#2A41E5]/10 text-[#2A41E5] text-sm font-medium mb-2">
                      {mission.type}
                    </div>
                    <p className="text-gray-500 text-sm">{mission.size}</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Enjeu</p>
                      <p className="text-gray-700 font-medium">{mission.challenge}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Ce qu'on a fait</p>
                      <p className="text-gray-600">{mission.solution}</p>
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Résultat</p>
                      <p className="text-[#2A41E5] font-semibold">{mission.result}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-12 text-center">
              Questions fréquentes
            </h2>
          </AnimatedSection>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <AnimatedSection key={idx} delay={idx * 100}>
                <div className="bg-[#F5F5F5] rounded-2xl border border-gray-200 overflow-hidden hover:border-[#2A41E5]/30 transition-colors">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                    <svg 
                      className={`w-5 h-5 text-[#2A41E5] transition-transform flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-6">
              Prêt à accélérer votre exécution commerciale ?
            </h2>
            <p className="text-lg text-white/60 mb-10 max-w-2xl mx-auto">
              Discutons de vos enjeux. On voit ensemble comment on peut vous aider.<br />
              Sans engagement.
            </p>
            <a
              href="https://meetings-eu1.hubspot.com/younes8" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              Réserver un échange
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Services liés */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#F5F5F5] to-white">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-3 block">
                Découvrez aussi
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900">Nos autres accompagnements</h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Link 
                to="/services/sparring" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Stratégie & Sparring Partner</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Mentorat stratégique pour dirigeants. Un partenaire exigeant pour prendre du recul et décider vite.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
              
              <Link 
                to="/services/architecte" 
                className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/20 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#2A41E5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#2A41E5] transition-colors">Ingénierie & Systèmes Commerciaux</h4>
                  <p className="text-gray-600 leading-relaxed mb-4">Conception de votre architecture commerciale : équipes, process, outils et playbooks sur-mesure.</p>
                  <div className="inline-flex items-center gap-2 text-[#2A41E5] font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center gap-6">
          <Logo variant="white" height={28} />
          <p className="text-gray-400">&copy; 2024 FOMOW. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default Pilote;
